import { toShortPath } from './recordCodes';
import { siteOrigin } from '../platform';

/**
 * Every in-app address keepr hands out, absolute and in its SHORT form when
 * the record's code is known (`/i/7k3qx9m` for `/collections/…/items/…`;
 * utils/recordCodes.js, KPE-002) — the long form otherwise, which opens the
 * same page.
 */
export const buildAbsoluteUrl = (path) => {
	if (!path) return '';
	const short = toShortPath(path);
	const origin = siteOrigin();
	return origin ? `${origin}${short}` : short;
};

// What a QR code's alphanumeric mode carries: digits, CAPITALS and these.
const QR_ALPHANUMERIC = /^[0-9A-Z $%*+\-./:]*$/;
// A bare record short URL — a letter and a code, nothing after them: the one
// shape whose every character reads the same in capitals (a code is
// case-insensitive; a path beneath it, a query or an /s/ code may not be).
const BARE_RECORD_PATH = /^\/[cidtef]\/[0-9a-hjkmnp-tv-z]{7,16}$/;

/**
 * The text a QR code should carry for `url`. A record's bare short URL goes in
 * capitals — `HTTPS://KEEPR.CLOUD/I/7K3QX9M` — because a QR code packs capitals
 * and digits (its alphanumeric mode) into about a size step smaller than the
 * same URL in lowercase; the scheme and host are case-insensitive, and so are
 * the letter and the code. Anything else goes exactly as it is.
 */
export const qrValue = (url) => {
	if (!url) return url;
	try {
		const parsed = new URL(url);
		if (parsed.search || parsed.hash || !BARE_RECORD_PATH.test(parsed.pathname)) return url;
		const upper = `${parsed.protocol}//${parsed.host}${parsed.pathname}`.toUpperCase();
		return QR_ALPHANUMERIC.test(upper) ? upper : url;
	} catch {
		return url;
	}
};

export const buildItemShareUrl = (collectionId, itemId) => {
	if (!itemId) return '';
	const normalizedCollectionId = collectionId || '';
	const path = `/collections/${normalizedCollectionId}/items/${itemId}`;
	return buildAbsoluteUrl(path);
};

export const buildCollectionShareUrl = (collectionId) => {
	if (!collectionId) return '';
	const path = `/collections/${collectionId}`;
	return buildAbsoluteUrl(path);
};

/** The authenticated deep link to a card definition. */
export const buildCardShareUrl = (cardId) => {
	if (!cardId) return '';
	return buildAbsoluteUrl(`/cards/${cardId}`);
};

/**
 * The public form of a short-link or share-link code. The API returns its own
 * `url` built from APP_BASE_URL; this is the fallback for when that variable
 * is unset (the code always comes back, the URL does not).
 */
export const buildShortLinkUrl = (code) => {
	if (!code) return '';
	return buildAbsoluteUrl(`/s/${code}`);
};
